import {
  ContentChild,
  ContentChildren,
  Directive,
  ElementRef,
  Input,
  QueryList,
  Renderer2
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { LoggingService } from '@com-delta-idp/mnx-utils';
import { Subscription } from 'rxjs';
import { ErrorHandlerHelperDirective } from './error-handler-helper.directive.';
import { ErrorField } from './errorfield.interface';
import { KeyValueFilterPipe } from './keyvaluefiler.pipe';
import { MakeCorrectionComponent } from './make-correction.component';
import { ValidationErrorMessageComponent } from './validation-error-message.component';

@Directive({
  selector: '[ngc-error-handler]',
  exportAs: 'ngcErrorHanglerDirective'
})
export class ErrorHandlerDirective {
  @ContentChildren(ValidationErrorMessageComponent, { descendants: true })
  errorMessageComponents: QueryList<ValidationErrorMessageComponent>;

  @ContentChild(MakeCorrectionComponent)
  makeCorrectionComponent: MakeCorrectionComponent;

  // tslint:disable-next-line:no-input-rename
  @Input('ngc-formToValidate') formToValidate: UntypedFormGroup;
  // tslint:disable-next-line:no-input-rename
  @Input('ngc-formNameToValidate') formToValidateName: string;
  @Input() makeVisible: boolean;
  public errorFields: ErrorField[] = [];
  private subscribeforFormChanges: Subscription;
  private setAriaAttributes = false;
  private subscribedForChanges = false;
  private queryListForMessagesInChildComponent: Map<
    number,
    ErrorHandlerHelperDirective
  > = new Map<number, ErrorHandlerHelperDirective>();

  private index = 0;
  private makeCorrectionClickCallbackRegistered = false;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private loggingService: LoggingService
  ) {}

  public removeErrorControl(controlName: string) {
    const field = this.errorFields.find((ef) => ef.fieldName === controlName);
    if (field != null) {
      const index = this.errorFields.indexOf(field);
      this.errorFields.splice(index, 1);
    }
  }

  public validateForm(forceValidation: boolean) {
    if (!this.makeCorrectionClickCallbackRegistered) {
      this.makeCorrectionComponent.onMakeCorrectionClickedEvent.subscribe(
        (event) => {
          if (event) {
            const fieldToFocus = this.elementRef.nativeElement.querySelector(
              '#' + event.focusedFieldId
            );
            if (fieldToFocus) {
              fieldToFocus.focus();
            }
          }
        },
        (err) => {
          this.loggingService.log('Caught Error: ' + err);
        }
      );
      this.makeCorrectionClickCallbackRegistered = true;
    }
    this.errorFields = [];
    if (forceValidation) {
      this.validateAllFormFields(this.formToValidate);
      if (!this.subscribedForChanges) {
        this.subscirbeForChanges();
      }
    }
    this.checkFormForErrors(this.formToValidate);
    // Add the errors on the main UntypedFormGroup
    if (this.formToValidate.errors != null) {
      const filter = new KeyValueFilterPipe();
      const keyValue = filter.transform(this.formToValidate.errors);
      this.addErrorControl(
        this.formToValidateName,
        this.formToValidate,
        keyValue.keys[0],
        keyValue.values[0]
      );
    }
    this.exportDataToMakeCorrectionComponent();
    if (forceValidation && this.makeCorrectionComponent) {
      this.makeCorrectionComponent.requestFocus();
    }
    this.setAriaAttributesForInputFields();
  }

  public removeAllFormControls() {
    for (const key in this.formToValidate.controls) {
      if (key) {
        this.formToValidate.removeControl(key);
      }
    }
  }

  public resetTheForm() {
    this.resetValidations(this.formToValidate);
    this.formToValidate.updateValueAndValidity();
    this.errorFields = [];
    this.exportDataToMakeCorrectionComponent();
  }

  public addErrorHandlerHelper(item: ErrorHandlerHelperDirective) {
    this.index++;
    this.queryListForMessagesInChildComponent.set(this.index, item);
    return this.index;
  }

  public removeErrorHanlderHelper(index: number) {
    this.queryListForMessagesInChildComponent.delete(index);
  }

  private subscirbeForChanges() {
    this.subscribedForChanges = true;
    this.subscribeforFormChanges = this.formToValidate.valueChanges.subscribe(
      (selectedValue) => {
        if (this.makeVisible) {
          this.validateForm(false);
        } else {
          this.errorFields = [];
          this.exportDataToMakeCorrectionComponent();
          this.subscribeforFormChanges.unsubscribe();
          this.subscribeforFormChanges = null;
          this.subscribedForChanges = false;
        }
      },
      (err) => {
        this.loggingService.log('Caught Error: ' + err);
      }
    );
  }

  private setAriaAttributesForInputFields() {
    const errorMessageComponents = this.errorMessageComponents.toArray();
    for (const errorMessageComponent of errorMessageComponents) {
      if (errorMessageComponent.focusedFieldId) {
        const fieldToFocus = this.elementRef.nativeElement.querySelector(
          '#' + errorMessageComponent.focusedFieldId
        );
        if (fieldToFocus) {
          this.renderer.setAttribute(
            fieldToFocus,
            'aria-describedby',
            errorMessageComponent.getId
          );
        }
      }
    }
  }

  private exportDataToMakeCorrectionComponent() {
    if (this.makeCorrectionComponent) {
      this.makeCorrectionComponent.numberOfErrors = this.errorFields.length;
      if (this.errorFields.length > 0) {
        this.makeCorrectionComponent.focusableFieldName =
          this.getNativeElementRefForFormControlName(
            this.errorFields[0].control
          );
      } else {
        this.makeCorrectionComponent.focusableFieldName = null;
      }
    }
  }

  private getNativeElementRefForFormControlName(control: AbstractControl) {
    const errorMessageComponents = this.getAllErrorMessageComponents();
    for (const e of errorMessageComponents) {
      if (e.control === control) {
        return e;
      }
    }
  }

  private validateAllFormFields(formGroup: AbstractControl) {
    if (
      formGroup instanceof UntypedFormGroup ||
      formGroup instanceof UntypedFormArray
    ) {
      Object.keys(formGroup.controls).forEach((field) => {
        const control = formGroup.get(field);
        if (control instanceof UntypedFormControl) {
          control.markAsTouched();
        } else if (
          control instanceof UntypedFormGroup ||
          control instanceof UntypedFormArray
        ) {
          this.validateAllFormFields(control);
        }
      });
    }
  }

  private checkFormForErrors(formGroup: UntypedFormGroup) {
    const errorMessage = this.getAllErrorMessageComponents();
    for (const e of errorMessage) {
      if (e.control.errors !== null) {
        const filter = new KeyValueFilterPipe();
        const keyValue = filter.transform(e.control.errors);
        this.addErrorControl(
          e.controlName,
          e.control,
          keyValue.keys[0],
          keyValue.values[0]
        );
      }
    }
  }

  private addErrorControl(
    controlName: string,
    control: AbstractControl,
    validatorName: string = null,
    validatorValue: string = null,
    foucsableFieldName = null,
    foucsableFieldNames: string[] = null
  ) {
    const found = this.errorFields.some(function (ef) {
      return ef.control === control;
    });
    if (!found) {
      const errorField = new ErrorField();
      errorField.fieldName = controlName;
      errorField.control = control;
      errorField.validatorName = validatorName;
      errorField.validatorValue = validatorValue !== null ? validatorValue : '';
      errorField.focusableField =
        foucsableFieldNames !== null
          ? foucsableFieldNames[0]
          : foucsableFieldName;
      this.errorFields.push(errorField);
    }
  }

  private resetValidations(formGroup: AbstractControl) {
    if (
      formGroup instanceof UntypedFormGroup ||
      formGroup instanceof UntypedFormArray
    ) {
      Object.keys(formGroup.controls).forEach((field) => {
        const control = formGroup.get(field);
        if (control.errors) {
          control.setErrors(null);
        }
        control.markAsPristine();
        control.markAsUntouched();
        control.updateValueAndValidity();
        this.resetValidations(control);
      });
      formGroup.updateValueAndValidity();
      formGroup.markAsUntouched();
      formGroup.markAsPristine();
    }
  }

  private getAllErrorMessageComponents() {
    const errorMessageComponents: ValidationErrorMessageComponent[] = [];
    if (this.queryListForMessagesInChildComponent) {
      this.queryListForMessagesInChildComponent.forEach(
        (entryVal, entryKey) => {
          const errorMessages = entryVal.getAllErrorMessageComponents();
          for (const ec of errorMessages) {
            errorMessageComponents.push(ec);
          }
        }
      );
    }
    const ecs = this.errorMessageComponents.toArray();
    for (const ec of ecs) {
      errorMessageComponents.push(ec);
    }
    return errorMessageComponents;
  }
}
